/**
 * DERAViariaR2Service — Geocodificador de carreteras por centroide de tramo
 *
 * Resuelve carreteras identificadas por código (A-348, AL-3400) o nombre
 * al centroide del tramo más cercano al municipio consultado.
 *
 * Los datos R2 agrupan segmentos por código de carretera (sin cod_mun).
 * La asignación municipal se resuelve por proximidad al centroide de
 * CENTROIDES_MUNICIPIOS (785 municipios, UTM30 EPSG:25830).
 *
 * Datos: DERA 09_01 Red Viaria — IECA (Junta de Andalucía)
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * URL Worker: /v1/dera/viaria  (sin .json.gz, el Worker hace mapping)
 * Key R2:     dera/viaria.json.gz  (sin prefijo v1/)
 * Precisión: ±500 m - 2 km (centroide de tramo)
 * Score: 50-65
 *
 * @module services/geocoding/specialized
 * @version 2.1.0
 * @date 2026-03-30
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  ViariaRawData,
  ViariaRawRoad,
  DERAViariaResult,
  ViariaSearchByCodeOptions,
  ViariaSearchByNameOptions,
} from '../../../types/deraViaria';
import {
  ROAD_CODE_IN_TEXT_REGEX,
  VIARIA_CENTROID_PRECISION_M,
  VIARIA_CODE_EXACT_CONFIDENCE,
  VIARIA_CODE_PARTIAL_CONFIDENCE,
  VIARIA_NAME_FUZZY_MAX_CONFIDENCE,
  VIARIA_X_RANGE,
  VIARIA_Y_RANGE,
  TIPO_MAP,
  TITULAR_MAP,
} from '../../../types/deraViaria';
import type { GeocodingResult } from '../../../types/infrastructure';
import { CENTROIDES_MUNICIPIOS } from '../../../lib/municipiosCentroides';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE_V1 } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE_V1`).
 */
const WORKER_BASE_URL = WORKER_BASE_V1;
const FETCH_TIMEOUT = 15_000;

/**
 * Techo de intentos de carga del dataset por sesión (AL51-FIX · DOP-AL51F-1=a).
 * El reintento ocurre en la siguiente invocación de `ensureLoaded` (típicamente
 * la fila siguiente del run): autorrepara el tropiezo transitorio sin martillear
 * un Worker caído. Cada intento ya lleva backoff HTTP interno (fetchWithBackoff).
 */
const MAX_LOAD_ATTEMPTS = 2;

/** Configuración uFuzzy para nombres de carreteras */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
};

// =============================================================================
// TIPO INTERNO — Carretera indexada con segmentos válidos
// =============================================================================

interface IndexedRoad {
  code: string;           // Código original ("A-348")
  normCode: string;       // Código normalizado ("a-348")
  road: ViariaRawRoad;    // Datos raw del road
  validSegs: [number, number][];  // Solo segmentos con coords válidas
}

// =============================================================================
// SINGLETON
// =============================================================================

let serviceInstance: DERAViariaR2Service | null = null;

export function getDERAViariaR2Service(): DERAViariaR2Service {
  if (!serviceInstance) {
    serviceInstance = new DERAViariaR2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetDERAViariaR2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class DERAViariaR2Service {
  private readonly baseUrl: string;
  private indexedRoads: IndexedRoad[] = [];
  private loaded = false;
  private loadPromise: Promise<void> | null = null;
  private loadAttempts = 0;
  private lastLoadError: string | null = null;

  // Índices
  private codeIndex = new Map<string, IndexedRoad>();           // normCode → road
  private fuzzy: uFuzzy | null = null;
  private fuzzyHaystack: string[] = [];       // "a-348 de berja a dalías"
  private fuzzyRoadRefs: IndexedRoad[] = [];  // parallel array para fuzzy

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
  }

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS
  // ---------------------------------------------------------------------------

  /**
   * Carga lazy del dataset viaria desde R2, con reintento acotado (AL51-FIX).
   * Si un intento previo falló, la siguiente invocación reintenta mientras
   * `loadAttempts < MAX_LOAD_ATTEMPTS`; agotado el techo, degrada definitivo
   * (sin martilleo) conservando `lastLoadError` para visibilidad.
   */
  async ensureLoaded(): Promise<void> {
    if (this.loaded) return;
    if (this.loadPromise) return this.loadPromise;
    if (this.loadAttempts >= MAX_LOAD_ATTEMPTS) return; // degradación definitiva
    this.loadPromise = this.fetchAndIndex();
    await this.loadPromise;
  }

  private async fetchAndIndex(): Promise<void> {
    const url = `${this.baseUrl}/dera/viaria`;
    try {
      const { response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT });

      if (!response.ok) {
        if (response.status === 404) {
          console.warn('[DERAViaria] Dataset viaria no disponible en R2 (404)');
          this.loaded = true;
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      // Clone defensivo: evita "Body already been read" si el runtime
      // consume el stream antes de que llegue aquí (Cloudflare Workers, CI).
      // Patrón idéntico al de CDAUR2Service.fetchMunicipio.
      let raw: ViariaRawData;
      try {
        const cloneable = typeof response.clone === 'function';
        raw = await (cloneable ? response.clone() : response).json() as ViariaRawData;
      } catch {
        // Fallback: body gzip crudo sin Content-Encoding (igual que CDAUR2Service)
        if (response.body && typeof DecompressionStream !== 'undefined') {
          const decompressed = response.body.pipeThrough(new DecompressionStream('gzip'));
          const text = await new Response(decompressed).text();
          raw = JSON.parse(text) as ViariaRawData;
        } else {
          throw new Error('[DERAViaria] Body ilegible y sin DecompressionStream');
        }
      }

      this.ingestRawData(raw);
      this.loaded = true;
      this.lastLoadError = null; // éxito → limpiar causa de fallos previos

      console.log(
        `[DERAViaria] Cargadas ${this.indexedRoads.length} carreteras ` +
        `(${this.totalValidSegments} segmentos válidos)`
      );
    } catch (error) {
      // AL51-FIX: NO fijar loaded=true — enmascaraba el fallo transitorio
      // (HX-FALLBACK-ENMASCARA-BUG, AUDIT_AL51_VIARIA §1). Reponer loadPromise
      // permite el reintento acotado en la siguiente invocación de ensureLoaded;
      // lastLoadError da visibilidad al orquestador (columna L del export).
      console.error('[DERAViaria] Error cargando dataset:', error);
      this.loadAttempts += 1;
      // Garantizar causa no vacía: hay errores con message vacío
      // (p. ej. DecompressionStream sobre payload gzip inválido).
      const causa = error instanceof Error ? error.message : String(error);
      this.lastLoadError = causa || `error de carga (${error?.constructor?.name ?? 'desconocido'})`;
      this.loadPromise = null;
    }
  }

  /** Ingesta y construcción de índices desde datos raw R2 */
  private ingestRawData(raw: ViariaRawData): void {
    this.indexedRoads = [];
    this.codeIndex.clear();

    for (const [code, road] of Object.entries(raw.roads)) {
      const validSegs = road.segs.filter(
        ([x, y]) =>
          x >= VIARIA_X_RANGE.min && x <= VIARIA_X_RANGE.max &&
          y >= VIARIA_Y_RANGE.min && y <= VIARIA_Y_RANGE.max
      );
      if (validSegs.length === 0) continue;

      const normCode = this.normalizeCode(code);
      const entry: IndexedRoad = { code, normCode, road, validSegs };
      this.indexedRoads.push(entry);
      this.codeIndex.set(normCode, entry);
    }

    this.buildFuzzyIndex();
  }

  /** Construye índice fuzzy sobre nombres de carreteras */
  private buildFuzzyIndex(): void {
    this.fuzzyHaystack = [];
    this.fuzzyRoadRefs = [];

    for (const entry of this.indexedRoads) {
      this.fuzzyHaystack.push(
        `${entry.code} ${entry.road.nombre}`.toLowerCase()
      );
      this.fuzzyRoadRefs.push(entry);
    }

    this.fuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  // ---------------------------------------------------------------------------
  // BÚSQUEDA POR CÓDIGO
  // ---------------------------------------------------------------------------

  /**
   * Busca carreteras por código oficial.
   * Match exacto normalizado: "A-348" = "a-348" = "A348"
   * Si codINE: devuelve segmento más cercano al centroide municipal.
   * Si no: devuelve centroide de todos los segmentos.
   */
  async searchByCode(options: ViariaSearchByCodeOptions): Promise<DERAViariaResult[]> {
    await this.ensureLoaded();
    const { code, codINE, maxResults = 5 } = options;
    const normCode = this.normalizeCode(code);
    if (!normCode) return [];

    // Match exacto
    const exact = this.codeIndex.get(normCode);
    if (exact) {
      return [this.buildResult(exact, 'code_exact', VIARIA_CODE_EXACT_CONFIDENCE, codINE)];
    }

    // Match parcial (prefijo)
    const partials: IndexedRoad[] = [];
    this.codeIndex.forEach((entry, key) => {
      if (key.startsWith(normCode)) {
        partials.push(entry);
      }
    });

    return partials
      .slice(0, maxResults)
      .map(entry =>
        this.buildResult(entry, 'code_partial', VIARIA_CODE_PARTIAL_CONFIDENCE, codINE)
      );
  }

  // ---------------------------------------------------------------------------
  // BÚSQUEDA POR NOMBRE
  // ---------------------------------------------------------------------------

  /**
   * Busca carreteras por nombre usando fuzzy matching.
   * Primero intenta extraer código del texto libre.
   * Ejemplo: "Berja Dalías" encuentra "DE BERJA A DALÍAS"
   */
  async searchByName(options: ViariaSearchByNameOptions): Promise<DERAViariaResult[]> {
    await this.ensureLoaded();
    const { name, codINE, minConfidence = 45, maxResults = 5 } = options;
    if (!name?.trim() || !this.fuzzy) return [];

    // Intentar extraer código del texto
    const extractedCode = this.extractCodeFromText(name);
    if (extractedCode) {
      const codeResults = await this.searchByCode({ code: extractedCode, codINE, maxResults });
      if (codeResults.length > 0) return codeResults;
    }

    // Fuzzy matching sobre haystack
    const needle = name.toLowerCase().trim();
    const searchResult = this.fuzzy.search(this.fuzzyHaystack, needle);
    if (!searchResult?.[0] || searchResult[0].length === 0) return [];

    const [idxs, _info, order] = searchResult;
    const processOrder = order ?? idxs;
    const results: DERAViariaResult[] = [];

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const haystackIdx = idxs[idx];
      const entry = this.fuzzyRoadRefs[haystackIdx];
      if (!entry) continue;

      const fuzzyScore = this.calcFuzzyScore(needle, entry);
      const confidence = Math.round(VIARIA_NAME_FUZZY_MAX_CONFIDENCE * Math.min(fuzzyScore, 1));
      if (confidence < minConfidence) continue;

      const result = this.buildResult(entry, 'name_fuzzy', confidence, codINE);
      result.fuzzyScore = fuzzyScore;
      results.push(result);
      if (results.length >= maxResults) break;
    }

    return results;
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN A GeocodingResult
  // ---------------------------------------------------------------------------

  /** Convierte el mejor resultado viaria a GeocodingResult estándar */
  toGeocodingResult(result: DERAViariaResult, originalQuery?: string): GeocodingResult {
    const r = result.road;
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'dera_viaria_r2',
      sourceLayer: 'viaria_09_01',
      originalQuery,
      matchedName: `${result.roadCode} — ${r.nombre}`,
      matchType: result.matchType,
      fuzzyScore: result.fuzzyScore,
      municipality: result.distToMuniCentroid !== undefined ? undefined : undefined,
      metadata: {
        codigo: result.roadCode,
        tipo: TIPO_MAP[r.tipo] ?? r.tipo,
        titular: TITULAR_MAP[r.titular] ?? r.titular,
        jerarquia: r.jerarquia,
        totalSegments: result.road.segs.length,
        precision: result.precision,
        fuente: 'DERA 09_01 Red Viaria',
      },
    };
  }

  // ---------------------------------------------------------------------------
  // CONSTRUCCIÓN DE RESULTADO
  // ---------------------------------------------------------------------------

  /**
   * Construye DERAViariaResult seleccionando el segmento óptimo.
   * Si codINE: segmento más cercano al centroide municipal.
   * Si no: centroide de todos los segmentos de la carretera.
   */
  private buildResult(
    entry: IndexedRoad,
    matchType: DERAViariaResult['matchType'],
    confidence: number,
    codINE?: string,
  ): DERAViariaResult {
    let x: number;
    let y: number;
    let distToMuniCentroid: number | undefined;

    if (codINE) {
      const centroid = CENTROIDES_MUNICIPIOS[codINE];
      if (centroid) {
        // Encontrar segmento más cercano al centroide municipal
        const nearest = this.findNearestSegment(entry.validSegs, centroid.x, centroid.y);
        x = nearest.x;
        y = nearest.y;
        distToMuniCentroid = nearest.dist;
      } else {
        // codINE no reconocido → usar centroide global
        const c = this.computeCentroid(entry.validSegs);
        x = c.x;
        y = c.y;
      }
    } else {
      // Sin filtro municipal → centroide de todos los segmentos
      const c = this.computeCentroid(entry.validSegs);
      x = c.x;
      y = c.y;
    }

    return {
      x,
      y,
      confidence,
      precision: VIARIA_CENTROID_PRECISION_M,
      source: 'dera_viaria_r2',
      roadCode: entry.code,
      road: entry.road,
      matchType,
      distToMuniCentroid,
    };
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES GEOMÉTRICAS
  // ---------------------------------------------------------------------------

  /** Encuentra el segmento más cercano a un punto de referencia */
  private findNearestSegment(
    segs: [number, number][],
    refX: number,
    refY: number,
  ): { x: number; y: number; dist: number } {
    let bestDist = Infinity;
    let bestX = segs[0][0];
    let bestY = segs[0][1];

    for (const [sx, sy] of segs) {
      const dx = sx - refX;
      const dy = sy - refY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < bestDist) {
        bestDist = dist;
        bestX = sx;
        bestY = sy;
      }
    }

    return { x: bestX, y: bestY, dist: Math.round(bestDist) };
  }

  /** Calcula centroide de un conjunto de segmentos */
  private computeCentroid(segs: [number, number][]): { x: number; y: number } {
    let sumX = 0;
    let sumY = 0;
    for (const [x, y] of segs) {
      sumX += x;
      sumY += y;
    }
    return {
      x: Math.round(sumX / segs.length),
      y: Math.round(sumY / segs.length),
    };
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES DE TEXTO
  // ---------------------------------------------------------------------------

  /** Normaliza código de carretera: "A - 348" → "a-348", "A348" → "a-348" */
  normalizeCode(code: string): string {
    if (!code) return '';
    let norm = code.trim().toLowerCase().replace(/\s+/g, '');
    // Insertar guion si falta entre letras y números: "a348" → "a-348"
    norm = norm.replace(/^([a-z]{1,2})(\d)/, '$1-$2');
    return norm;
  }

  /** Extrae código de carretera de texto libre */
  extractCodeFromText(text: string): string | null {
    const match = text.match(ROAD_CODE_IN_TEXT_REGEX);
    return match ? match[1] : null;
  }

  /** Calcula score fuzzy simplificado (0-1) basado en haystack */
  private calcFuzzyScore(needle: string, entry: IndexedRoad): number {
    const combined = `${entry.code} ${entry.road.nombre}`.toLowerCase();
    if (combined === needle) return 1.0;
    if (combined.includes(needle)) return 0.9;
    if (needle.includes(entry.normCode)) return 0.85;
    const ratio = needle.length / Math.max(combined.length, needle.length);
    return Math.min(0.8, 0.4 + ratio * 0.4);
  }

  // ---------------------------------------------------------------------------
  // ESTADO Y CACHE
  // ---------------------------------------------------------------------------

  /** Total de segmentos válidos cargados */
  get totalValidSegments(): number {
    return this.indexedRoads.reduce((sum, r) => sum + r.validSegs.length, 0);
  }

  /** Número de carreteras indexadas */
  get totalRoads(): number {
    return this.indexedRoads.length;
  }

  /** Indica si los datos están cargados */
  get isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Causa del último fallo de carga del dataset (AL51-FIX).
   * `null` si la carga fue correcta o aún no se ha intentado.
   */
  getLoadError(): string | null {
    return this.lastLoadError;
  }

  /** Limpia datos e índices */
  clearCache(): void {
    this.indexedRoads = [];
    this.codeIndex.clear();
    this.fuzzy = null;
    this.fuzzyHaystack = [];
    this.fuzzyRoadRefs = [];
    this.loaded = false;
    this.loadPromise = null;
    this.loadAttempts = 0;
    this.lastLoadError = null;
  }

  /**
   * Carga datos directamente desde ViariaRawData (para tests sin fetch).
   */
  loadFromRawData(raw: ViariaRawData): void {
    this.ingestRawData(raw);
    this.loaded = true;
  }
}

export default DERAViariaR2Service;
